import React, { useEffect, useMemo, useState } from 'react';
import './TemplateMailRulesEditor.css';
import SegmentedControl from '../input/SegmentedControl';
import TemplateTextblockSelect from './TemplateTextblockSelect';
import { mailRuleKey } from './templateModel';

const ALL_SCOPE = 'all';

// Explicit slots ignore "Enabled" — the rule only provides the textblock.
const EXPLICIT_SLOT_HINT = {
  payment_confirmed: 'Wird nur im Zahlungsdialog ausdrücklich versendet – hier zählt nur der Baustein.',
  announcement: 'Baustein der Prozess-Mails, wenn ein Mail-Schritt keinen eigenen Baustein hat.',
};
const EXPLICIT_FALLBACK_HINT = 'Wird nur ausdrücklich versendet – hier zählt nur der Baustein.';

const isSet = (rule) => !!rule && (rule.enabled != null || rule.textblockId != null);
const onOff = (value) => (value ? 'an' : 'aus');

/**
 * Mails tab of the template editor: overrides per mail slot × scope ("Alle Gruppen" or one group).
 * Enabled is tri-state (Erben/An/Aus), the textblock select's first option inherits (null).
 * Resolution on the server (IMailPolicy): template + group → template + all groups → group rule;
 * Enabled and textblock independently. There is no code default: without a textblock no mail is sent.
 * To explain "Erben", the group's own rules are loaded once per group (useTemplateReferenceData) —
 * without them the row just says "Regel der Gruppe".
 */
function TemplateMailRulesEditor({ mailRules, onPatchRule, onResetScope, groups, slots, textblocks, groupMailRules, onNeedGroupRules }) {
  const [scope, setScope] = useState(ALL_SCOPE);

  const ruleOf = (slot, groupId) => mailRules[mailRuleKey(slot, groupId)] ?? { enabled: null, textblockId: null };

  // Groups with overrides (also archived ones, so their rules stay visible and removable).
  const scopeGroupIds = new Set(Object.keys(mailRules)
    .filter(key => isSet(mailRules[key]))
    .map(key => key.split('|')[1])
    .filter(part => part !== ALL_SCOPE)
    .map(Number));
  const scopeGroups = groups.filter(g => !g.isArchived || scopeGroupIds.has(g.id));
  const activeGroups = groups.filter(g => !g.isArchived);
  const scopeGroupKey = scopeGroups.map(g => g.id).join(',');

  useEffect(() => {
    if (scopeGroupKey) onNeedGroupRules?.(scopeGroupKey.split(',').map(Number));
  }, [scopeGroupKey, onNeedGroupRules]);

  // Selected group vanished (e.g. its last override removed while archived) → back to "all".
  const scopeGroup = scope === ALL_SCOPE ? null : scopeGroups.find(g => String(g.id) === scope) ?? null;
  const groupId = scopeGroup ? scopeGroup.id : null;

  const tbById = useMemo(() => new Map((textblocks ?? []).map(tb => [tb.id, tb])), [textblocks]);
  const tbLabel = (id) => tbById.get(Number(id))?.label ?? `Baustein #${id}`;

  const countFor = (gid) => {
    const suffix = `|${gid ?? ALL_SCOPE}`;
    return Object.keys(mailRules).filter(key => key.endsWith(suffix) && isSet(mailRules[key])).length;
  };
  const withCount = (label, n) => (n > 0 ? `${label} (${n})` : label);

  const scopeOptions = [
    { value: ALL_SCOPE, label: withCount('Alle Gruppen', countFor(null)), icon: 'groups' },
    ...scopeGroups.map(g => ({
      value: String(g.id),
      label: withCount(g.isArchived ? `${g.name} (archiviert)` : g.name, countFor(g.id)),
    })),
  ];

  // What a row inherits when its own value is null.
  const inheritedFor = (slot) => {
    if (groupId == null) {
      const parts = activeGroups.map(g => {
        const own = groupMailRules?.[g.id]?.[slot.slot];
        return own ? `${g.name} ${onOff(own.enabled)}` : null;
      });
      const known = parts.length > 0 && parts.every(Boolean);
      return {
        enabledShort: null,
        enabledText: known ? `Ohne Einstellung gilt die Regel je Gruppe: ${parts.join(', ')}.` : 'Ohne Einstellung gilt die Regel der jeweiligen Gruppe.',
        textblockText: 'Erben (Baustein der jeweiligen Gruppe)',
      };
    }

    const all = ruleOf(slot.slot, null);
    const own = groupMailRules?.[groupId]?.[slot.slot];
    let enabledShort = null;
    let enabledText = `Ohne Einstellung gilt die Regel der Gruppe „${scopeGroup.name}“.`;
    if (all.enabled != null) {
      enabledShort = onOff(all.enabled);
      enabledText = `Ohne Einstellung: ${enabledShort} – aus „Alle Gruppen“ dieser Vorlage.`;
    } else if (own) {
      enabledShort = onOff(own.enabled);
      enabledText = `Ohne Einstellung: ${enabledShort} – Regel der Gruppe „${scopeGroup.name}“.`;
    }

    let textblockText = 'Erben (Regel der Gruppe)';
    if (all.textblockId != null) {
      textblockText = `Erben (${tbLabel(all.textblockId)} – aus „Alle Gruppen“)`;
    } else if (own?.textblockId != null) {
      textblockText = `Erben (${tbLabel(own.textblockId)} – Regel der Gruppe)`;
    } else if (own) {
      textblockText = 'Erben (kein Textbaustein – keine Mail)';
    }
    return { enabledShort, enabledText, textblockText };
  };

  // Groups of the current scope for which an automatic slot ends up switched on without a textblock
  // (→ no mail). Only groups whose own rules are loaded count; explicit slots are left out (a mail step
  // or the payment dialog decides whether they are sent at all).
  const noMailGroups = (slot) => {
    if (slot.isExplicit) return [];
    const all = ruleOf(slot.slot, null);
    return (scopeGroup ? [scopeGroup] : activeGroups).filter(g => {
      const own = groupMailRules?.[g.id]?.[slot.slot];
      if (!own) return false;
      const specific = ruleOf(slot.slot, g.id);
      const enabled = specific.enabled ?? all.enabled ?? own.enabled;
      const textblockId = specific.textblockId ?? all.textblockId ?? own.textblockId;
      return !!enabled && textblockId == null;
    });
  };

  const noMailWarning = (slot) => {
    const affected = noMailGroups(slot);
    if (affected.length === 0) return null;
    return scopeGroup
      ? 'Ohne Textbaustein wird keine Mail gesendet.'
      : `Ohne Textbaustein wird keine Mail gesendet – betrifft ${affected.map(g => `„${g.name}“`).join(', ')}.`;
  };

  const scopeCount = countFor(groupId);

  return (
    <div className="ptMailRules">
      <div className="ptMailLiveHint" role="note">
        <span className="material-symbols-rounded" aria-hidden="true">bolt</span>
        <span>
          Mailregeln werden live gelesen: Nach dem Speichern wirken sie sofort auf alle Events dieser Vorlage – auch in
          laufenden Prozessen. Preise wirken dagegen nur, wenn Events neu angelegt werden.
        </span>
      </div>

      <div className="ptMailScope">
        <SegmentedControl
          size="sm"
          ariaLabel="Mailregeln für"
          value={scopeGroup ? String(scopeGroup.id) : ALL_SCOPE}
          onChange={setScope}
          options={scopeOptions}
        />
      </div>
      <div className="ptMailScopeBar">
        <p className="formFieldHint">
          {scopeGroup
            ? `Nur für „${scopeGroup.name}“ – hat Vorrang vor „Alle Gruppen“ und vor der Regel der Gruppe.`
            : '„Alle Gruppen“ überschreibt für Events dieser Vorlage die Mailregeln jeder Gruppe. Einstellungen einer einzelnen Gruppe haben Vorrang.'}
        </p>
        {scopeCount > 0 && (
          <button type="button" className="ptMailResetBtn" onClick={() => onResetScope?.(groupId)}>
            <span className="material-symbols-rounded" aria-hidden="true">restart_alt</span>
            Alles erben
          </button>
        )}
      </div>

      {!slots ? (
        <p className="ptMailMessage">Mail-Arten werden geladen …</p>
      ) : (
        <ul className="ptMailList">
          {slots.map(slot => {
            const key = mailRuleKey(slot.slot, groupId);
            const rule = ruleOf(slot.slot, groupId);
            const inherited = inheritedFor(slot);
            const tri = rule.enabled == null ? 'inherit' : (rule.enabled ? 'on' : 'off');
            const warning = noMailWarning(slot);
            return (
              <li key={slot.slot} className={`ptMailRow${isSet(rule) ? ' isOverridden' : ''}`}>
                <div className="ptMailSlot">
                  <span className="ptMailSlotLabel">{slot.label}</span>
                  <span className="ptMailSlotHint">
                    {slot.isExplicit ? (EXPLICIT_SLOT_HINT[slot.slot] ?? EXPLICIT_FALLBACK_HINT) : inherited.enabledText}
                  </span>
                </div>
                <div className="ptMailEnabled">
                  {slot.isExplicit ? (
                    <span className="ptMailExplicit">Ausdrücklich</span>
                  ) : (
                    <SegmentedControl
                      size="sm"
                      ariaLabel={`${slot.label}: versenden`}
                      value={tri}
                      onChange={v => onPatchRule(key, { enabled: v === 'inherit' ? null : v === 'on' })}
                      options={[
                        { value: 'inherit', label: inherited.enabledShort ? `Erben (${inherited.enabledShort})` : 'Erben' },
                        { value: 'on', label: 'An', tone: 'positive' },
                        { value: 'off', label: 'Aus', tone: 'negative' },
                      ]}
                    />
                  )}
                </div>
                <TemplateTextblockSelect
                  label="Textbaustein"
                  value={rule.textblockId}
                  textblocks={textblocks}
                  emptyLabel={inherited.textblockText}
                  onChange={id => onPatchRule(key, { textblockId: id })}
                />
                {warning && (
                  <p className="ptMailRowWarning">
                    <span className="material-symbols-rounded" aria-hidden="true">warning</span>
                    {warning}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default TemplateMailRulesEditor;
